import React, { useEffect, useState, useRef } from 'react';
import { api } from '../../shared/utils/api';
import { LoadingSpinner } from '../../shared/components/ui';
import ROUTES from '../../shared/constants/routes';

const BAND_VALUES = { 1: 0.1, 2: 0.3, 3: 0.5, 4: 0.7, 5: 0.9 };

function nearestBand(conf) {
  if (conf === null || conf === undefined) return null;
  if (conf < 0.2) return 1;
  if (conf < 0.4) return 2;
  if (conf < 0.6) return 3;
  if (conf < 0.8) return 4;
  return 5;
}

function getAllIds(areas) {
  return areas.flatMap(a => (a.study_units || []).map(su => su.id));
}

function Topbar() {
  return (
    <div className="rr-topbar">
      <svg viewBox="0 0 32 32" width="18" height="18" aria-hidden="true">
        <polygon points="3,27 12,27 17,15 8,15" fill="rgba(255,255,255,0.3)" />
        <polygon points="10,23 19,23 24,11 15,11" fill="rgba(255,255,255,0.65)" />
        <polygon points="17,19 26,19 31,7 22,7" fill="rgba(255,255,255,0.95)" />
      </svg>
      <span className="rr-topbar-text">
        <span className="rr-topbar-sqe">SQE</span>
        <span className="rr-topbar-track">Track</span>
      </span>
    </div>
  );
}

export default function RefineRatingsPage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [flk1Areas, setFlk1Areas] = useState([]);
  const [flk2Areas, setFlk2Areas] = useState([]);
  const [activeTab, setActiveTab] = useState('flk1');
  const [openSections, setOpenSections] = useState(new Set());
  const [ratings, setRatings] = useState({});
  const [savedStates, setSavedStates] = useState({});
  const [sessionRated, setSessionRated] = useState(new Set());
  const [flk1Banner, setFlk1Banner] = useState(false);
  const autoSwitchTimer = useRef(null);
  const completionSent = useRef(false);
  const hasInteracted = useRef(false);

  useEffect(() => {
    api('/api/refine-ratings?token=' + encodeURIComponent(token), { skipAuth: true })
      .then(res => {
        if (!res.ok) {
          setExpired(true);
        } else {
          const allAreas = res.areas || [];
          const flk1 = allAreas.filter(a => (a.exam || '').toUpperCase() === 'FLK1');
          const flk2 = allAreas.filter(a => (a.exam || '').toUpperCase() === 'FLK2');
          setFlk1Areas(flk1);
          setFlk2Areas(flk2);

          const initial = {};
          const initialSessionRated = new Set();
          allAreas.forEach(area => {
            (area.study_units || []).forEach(su => {
              const band = nearestBand(su.avg_confidence);
              if (band !== null) {
                initial[su.id] = band;
                initialSessionRated.add(su.id);
              }
            });
          });
          setRatings(initial);
          setSessionRated(initialSessionRated);

          const initialOpen = new Set();
          if (flk1.length > 0) initialOpen.add(flk1[0].subject_area);
          if (flk2.length > 0) initialOpen.add(flk2[0].subject_area);
          setOpenSections(initialOpen);
        }
      })
      .catch(() => setExpired(true))
      .finally(() => setLoading(false));

    return () => {
      if (autoSwitchTimer.current) clearTimeout(autoSwitchTimer.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTab = (tab) => {
    if (autoSwitchTimer.current) {
      clearTimeout(autoSwitchTimer.current);
      autoSwitchTimer.current = null;
    }
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleSection = (key) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleRate = async (study_unit_id, band) => {
    hasInteracted.current = true;
    const newSessionRated = new Set([...sessionRated, study_unit_id]);
    setSessionRated(newSessionRated);
    setRatings(prev => ({ ...prev, [study_unit_id]: band }));
    setSavedStates(prev => ({ ...prev, [study_unit_id]: null }));

    const flk1Ids = getAllIds(flk1Areas);
    const flk2Ids = getAllIds(flk2Areas);
    const flk1RatedCount = flk1Ids.filter(id => newSessionRated.has(id)).length;
    if (!flk1Banner && flk1Ids.length > 0 && flk1RatedCount === flk1Ids.length) {
      setFlk1Banner(true);
      autoSwitchTimer.current = setTimeout(() => setActiveTab('flk2'), 2000);
    }

    const total = flk1Ids.length + flk2Ids.length;
    const allNowDone = total > 0
      && flk1Ids.every(id => newSessionRated.has(id))
      && flk2Ids.every(id => newSessionRated.has(id));

    try {
      const body = { ratings: [{ study_unit_id, confidence: BAND_VALUES[band] }] };
      if (allNowDone && !completionSent.current) {
        completionSent.current = true;
        body.complete = true;
      }
      const res = await api('/api/refine-ratings?token=' + encodeURIComponent(token), {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      });
      if (res.ok) {
        setSavedStates(prev => ({ ...prev, [study_unit_id]: 'saved' }));
        setTimeout(() => setSavedStates(prev => ({ ...prev, [study_unit_id]: null })), 800);
      } else {
        setSavedStates(prev => ({ ...prev, [study_unit_id]: 'error' }));
      }
    } catch {
      setSavedStates(prev => ({ ...prev, [study_unit_id]: 'error' }));
    }
  };

  const flk1Ids = getAllIds(flk1Areas);
  const flk2Ids = getAllIds(flk2Areas);
  const flk1Total = flk1Ids.length;
  const flk2Total = flk2Ids.length;
  const flk1Rated = flk1Ids.filter(id => sessionRated.has(id)).length;
  const flk2Rated = flk2Ids.filter(id => sessionRated.has(id)).length;
  const grandTotal = flk1Total + flk2Total;
  const allDone = grandTotal > 0 && flk1Rated === flk1Total && flk2Rated === flk2Total;

  if (loading) {
    return (
      <div className="rr-page">
        <Topbar />
        <div className="rr-state-center"><LoadingSpinner /></div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="rr-page">
        <Topbar />
        <div className="rr-state-center">
          <p className="rr-state-msg">This link has expired. Log in to access your planner.</p>
          <a href={ROUTES.LOGIN} className="rr-link">Log in</a>
        </div>
      </div>
    );
  }

  if (allDone && hasInteracted.current) {
    return (
      <div className="rr-page">
        <Topbar />
        <div className="rr-state-center">
          <p className="rr-state-msg">You're all set. Your revision plan is now personalised.</p>
          <a href={ROUTES.LOGIN} className="rr-link">Go to my planner</a>
        </div>
      </div>
    );
  }

  const renderTabContent = (areas, tabKey) => {
    const tabIds = getAllIds(areas);
    const tabRated = tabIds.filter(id => sessionRated.has(id)).length;
    const tabTotal = tabIds.length;
    const pct = tabTotal > 0 ? Math.round((tabRated / tabTotal) * 100) : 0;

    return (
      <>
        {tabKey === 'flk1' && flk1Banner && (
          <div className="rr-complete-banner">
            <p>FLK1 done.</p>
            <span>Moving to <button className="rr-switch-link" onClick={() => switchTab('flk2')}>FLK2</button>.</span>
          </div>
        )}

        <div className="rr-progress">
          <div className="rr-bar-track">
            <div className="rr-bar-fill" style={{ width: pct + '%' }} />
          </div>
          <span className="rr-progress-label">{tabRated} of {tabTotal} rated</span>
        </div>

        {areas.map(area => {
          const units = area.study_units || [];
          const sectionRated = units.filter(su => sessionRated.has(su.id)).length;
          const isComplete = units.length > 0 && sectionRated === units.length;
          const isOpen = openSections.has(area.subject_area);

          return (
            <div key={area.subject_area} className={`rr-section${isOpen ? ' open' : ''}`}>
              <div className="rr-section-header" onClick={() => toggleSection(area.subject_area)}>
                <div className="rr-section-header-left">
                  <span className="rr-section-title">{area.subject_area}</span>
                  <span className="rr-section-count">{units.length} topics</span>
                </div>
                <div className="rr-section-header-right">
                  <span className={`rr-section-badge${isComplete ? ' complete' : ''}`}>
                    {sectionRated} / {units.length}
                  </span>
                  <svg className="rr-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
              <div className="rr-section-body">
                {units.map(su => {
                  const selected = ratings[su.id];
                  const saved = savedStates[su.id];
                  return (
                    <div key={su.id} className="rr-unit-row">
                      <span className="rr-unit-name">{su.name}</span>
                      <div className="rr-unit-right">
                        <div className="rr-rating-group">
                          {[1, 2, 3, 4, 5].map(band => (
                            <button
                              key={band}
                              className={`rr-btn${selected === band ? ' selected' : ''}`}
                              onClick={() => handleRate(su.id, band)}
                            >
                              {band}
                            </button>
                          ))}
                        </div>
                        <span className={`rr-saved-indicator${saved === 'saved' ? ' show' : ''}${saved === 'error' ? ' error' : ''}`}>
                          {saved === 'error' ? 'Failed to save' : 'Saved'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="rr-page">
      <Topbar />
      <div className="rr-container">
        <div className="rr-page-header">
          <h1 className="rr-page-title">Rate your study topics</h1>
          <p className="rr-page-subtitle">
            Tell us how confident you feel on each topic. 1 is shaky, 5 is solid. This gives your planner a starting point.
          </p>
        </div>

        <div className="rr-tabs">
          <button
            className={`rr-tab${activeTab === 'flk1' ? ' active' : ''}`}
            onClick={() => switchTab('flk1')}
          >
            FLK1
            <span className="rr-tab-meta">{flk1Rated} / {flk1Total} rated</span>
          </button>
          <button
            className={`rr-tab${activeTab === 'flk2' ? ' active' : ''}`}
            onClick={() => switchTab('flk2')}
          >
            FLK2
            <span className="rr-tab-meta">{flk2Rated} / {flk2Total} rated</span>
          </button>
        </div>

        {activeTab === 'flk1' && renderTabContent(flk1Areas, 'flk1')}
        {activeTab === 'flk2' && renderTabContent(flk2Areas, 'flk2')}

        <p className="rr-footer-note">Your progress saves automatically. Close this page and come back any time.</p>
        <div className="rr-footer-action">
          <a href={ROUTES.LOGIN} className="rr-link">Go to SQETrack</a>
        </div>
      </div>
    </div>
  );
}
